import { Injectable } from '@angular/core';
import { environment } from 'projects/sharing/environments/environment';
import { API } from '../configs/api.config';
import { LS } from '../enums/local-storage.enum';
import { MenuNavbarAction, MenuNavbarListOutput } from '../models/menu.model';
import { GeneralService } from './general.service';

/** A menu and the action the caller is allowed to invoke inside it. */
export interface MenuActionPair {
  menuId: string;
  actionId: string;
}

/**
 * A pair resolved for a request, plus how it was found. `matched` is false
 * when no menu registers the endpoint and the action is only the menu's
 * contextual fallback — a guess an explicit page rule should be allowed to
 * override.
 */
export interface ResolvedMenuAction extends MenuActionPair {
  matched: boolean;
}

type MenuActionMatch = {
  menu: MenuNavbarListOutput;
  action: MenuNavbarAction;
};

const API_BASE_URLS = [
  API.BASE_URL,
  API.AUTH_BASE_URL,
  API.EMAIL_BASE_URL,
  API.MERCHANT_BASE_URL,
  API.FILE_BASE_URL,
  API.PRODUCT_BASE_URL,
  API.ROLE_BASE_URL,
  API.MENU_BASE_URL,
  API.NOTIFICATION_BASE_URL,
  API.TRANSACTION_BASE_URL,
  API.SETTLEMENT_BASE_URL,
  API.PAYMENT_SIMULATOR_BASE_URL,
];

const API_ENDPOINT_PREFIXES = [
  '/auth',
  '/email',
  '/file',
  '/menu',
  '/merchant',
  '/notification',
  '/payment',
  '/product',
  '/que-notification',
  '/role',
  '/settlement',
  '/transaction',
  '/user',
];

/**
 * Owns the menu tree the nav-bar returned and turns it into the
 * `X-Menu-Id` / `X-Action-Id` pair a request must carry.
 *
 * `resolveForRequest()` answers the question the interceptor asks: which menu
 * and action may be used for a given endpoint while the user stands on a given
 * route. When nothing matches — the auth pages, and the bootstrap calls fired
 * right after login — `getLoginPair()` is the pair that applies.
 */
@Injectable({
  providedIn: 'root',
})
export class PermissionContextService {
  private menus: MenuNavbarListOutput[] = [];

  constructor(private readonly generalService: GeneralService) {}

  // ─── Public API ────────────────────────────────────────────────────────────

  /** Replaces the in-memory menu tree with the nav-bar response. */
  setMenuGroups(groups: MenuNavbarListOutput[]): void {
    this.menus = flattenMenus(Array.isArray(groups) ? groups : []);
  }

  /**
   * The menu/action the user may use for `requestUrl` while standing on
   * `routePath`. Returns null when neither the active menu nor any other menu
   * registers the endpoint.
   */
  resolveForRequest(
    routePath: string,
    requestUrl: string,
  ): ResolvedMenuAction | null {
    const endpointPath = normalizeRequestEndpoint(requestUrl);
    const activeMenu = findActiveMenu(
      this.menus,
      normalizeRoutePath(routePath),
    );
    const activeActionId = findActiveEndpointActionId(activeMenu, endpointPath);
    const actionMatch = activeActionId
      ? null
      : findEndpointAction(this.menus, endpointPath);
    const matchedActionId = activeActionId || actionMatch?.action.actionId;
    const actionId =
      matchedActionId || findContextualFallbackActionId(activeMenu);
    const menuId = activeMenu?.id ?? actionMatch?.menu.id ?? '';

    if (!menuId && !actionId) {
      return null;
    }

    return { menuId, actionId, matched: Boolean(matchedActionId) };
  }

  /**
   * The pair for requests that run outside the menu tree: login, register,
   * forgot password and the other auth pages, plus the bootstrap calls fired
   * right after login before any menu is active.
   */
  getLoginPair(): MenuActionPair {
    return {
      menuId: environment.LOGIN_MENU_ID,
      actionId: environment.LOGIN_ACTION_ID,
    };
  }

  /** Rehydrates the menu tree from localStorage after a page reload. */
  restoreMenus(): void {
    const stored = window.localStorage.getItem(LS.MENU_LIST);
    if (!stored || !this.generalService.isJSON(stored)) {
      return;
    }

    this.setMenuGroups(JSON.parse(stored));
  }
}

// ─── Menu lookup ─────────────────────────────────────────────────────────────

function findActiveMenu(
  menus: MenuNavbarListOutput[],
  routePath: string,
): MenuNavbarListOutput | null {
  return (
    menus
      .filter((menu) => routeMatchesMenuLink(routePath, menu.link))
      .sort((left, right) => linkLength(right) - linkLength(left))[0] ?? null
  );
}

function flattenMenus(
  menus: MenuNavbarListOutput[] | null | undefined,
): MenuNavbarListOutput[] {
  const safeMenus = Array.isArray(menus) ? menus : [];
  return safeMenus.flatMap((menu) => [menu, ...flattenMenus(menu.navItems)]);
}

function linkLength(menu: MenuNavbarListOutput): number {
  return normalizeRoutePath(menu.link ?? '').length;
}

function routeMatchesMenuLink(routePath: string, link?: string): boolean {
  const rawLink = (link ?? '').trim();
  if (!rawLink || rawLink === '-' || /^https?:\/\//i.test(rawLink)) {
    return false;
  }

  const normalizedLink = normalizeRoutePath(rawLink);
  return (
    routePath === normalizedLink || routePath.startsWith(`${normalizedLink}/`)
  );
}

function findEndpointAction(
  menus: MenuNavbarListOutput[],
  endpointPath: string,
): MenuActionMatch | null {
  for (const menu of menus) {
    const action = (menu.actions ?? []).find((item) =>
      endpointListMatches(item.endpointPath, endpointPath),
    );
    if (action) {
      return { menu, action };
    }
  }

  return null;
}

function findActiveEndpointActionId(
  menu: MenuNavbarListOutput | null,
  endpointPath: string,
): string {
  return menu ? findActiveMenuEndpointActionId(menu.actions, endpointPath) : '';
}

function findContextualFallbackActionId(
  menu: MenuNavbarListOutput | null,
): string {
  if (!menu) {
    return '';
  }

  return (
    findDefaultEndpointActionId(menu.actions) ||
    findDefaultViewActionId(menu.actions)
  );
}

function findActiveMenuEndpointActionId(
  actions: MenuNavbarAction[] | undefined,
  endpointPath: string,
): string {
  const safeActions = actions ?? [];
  const exactAction = safeActions.find((item) =>
    endpointListMatches(item.endpointPath, endpointPath),
  );
  const relativeAction = safeActions.find((item) =>
    relativeEndpointListMatches(item.endpointPath, endpointPath),
  );
  return exactAction?.actionId ?? relativeAction?.actionId ?? '';
}

function findDefaultEndpointActionId(
  actions: MenuNavbarAction[] | undefined,
): string {
  const action = (actions ?? []).find((item) =>
    defaultEndpointListMatches(item.endpointPath),
  );
  return action?.actionId ?? '';
}

function findDefaultViewActionId(
  actions: MenuNavbarAction[] | undefined,
): string {
  const action = (actions ?? []).find((item) =>
    normalizeActionName(item.actionName).includes('view'),
  );
  return action?.actionId ?? '';
}

function normalizeActionName(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

// ─── Endpoint matching ───────────────────────────────────────────────────────

function endpointListMatches(
  endpointPaths: string[] | undefined,
  endpointPath: string,
): boolean {
  return (endpointPaths ?? []).some((item) =>
    endpointMatches(endpointPath, item),
  );
}

function relativeEndpointListMatches(
  endpointPaths: string[] | undefined,
  endpointPath: string,
): boolean {
  return (endpointPaths ?? []).some((item) =>
    relativeEndpointMatches(endpointPath, item),
  );
}

function defaultEndpointListMatches(
  endpointPaths: string[] | undefined,
): boolean {
  return (endpointPaths ?? []).some(
    (item) => normalizeRequestEndpoint(item) === '/',
  );
}

function endpointMatches(
  requestEndpoint: string,
  configuredEndpoint: string,
): boolean {
  const endpoint = normalizeRequestEndpoint(configuredEndpoint);
  return (
    endpoint === requestEndpoint ||
    endpointTemplateMatches(requestEndpoint, endpoint)
  );
}

function relativeEndpointMatches(
  requestEndpoint: string,
  configuredEndpoint: string,
): boolean {
  const endpoint = normalizeRequestEndpoint(configuredEndpoint);
  if (!isRelativeEndpointShortcut(endpoint)) {
    return false;
  }

  return requestEndpoint === endpoint || requestEndpoint.endsWith(endpoint);
}

function isRelativeEndpointShortcut(endpoint: string): boolean {
  return endpoint !== '/' && splitEndpoint(endpoint).length === 1;
}

function endpointTemplateMatches(
  requestEndpoint: string,
  template: string,
): boolean {
  const requestSegments = splitEndpoint(requestEndpoint);
  const templateSegments = splitEndpoint(template);
  return (
    requestSegments.length === templateSegments.length &&
    templateSegments.every((item, index) =>
      segmentMatches(requestSegments[index], item),
    )
  );
}

function segmentMatches(segment: string, templateSegment: string): boolean {
  return segment === templateSegment || /^\{[^/{}]+\}$/.test(templateSegment);
}

function splitEndpoint(endpoint: string): string[] {
  return endpoint.split('/').filter(Boolean);
}

// ─── Path helpers ────────────────────────────────────────────────────────────

export function isBackendApiRequest(url: string): boolean {
  const path = normalizePath(getPathOnly(url));
  if (path.startsWith('/api/')) {
    return true;
  }

  const endpointPath = stripKnownApiBase(path);
  return API_ENDPOINT_PREFIXES.some((item) =>
    endpointHasPrefix(endpointPath, item),
  );
}

export function normalizeRequestEndpoint(url: string): string {
  return stripKnownApiBase(normalizePath(getPathOnly(url)));
}

export function normalizeRoutePath(path: string): string {
  return normalizePath(getPathOnly(path));
}

function endpointHasPrefix(endpointPath: string, prefix: string): boolean {
  return endpointPath === prefix || endpointPath.startsWith(`${prefix}/`);
}

function stripKnownApiBase(path: string): string {
  const match = normalizedApiBaseUrls()
    .map((basePath) => stripBasePath(path, basePath))
    .find((item): item is string => item !== null);
  return match ?? stripProxyApiBase(path);
}

function normalizedApiBaseUrls(): string[] {
  return API_BASE_URLS.map(getPathOnly)
    .map(normalizePath)
    .filter((item) => item !== '/')
    .sort((left, right) => right.length - left.length);
}

function stripBasePath(path: string, basePath: string): string | null {
  if (path === basePath) {
    return '/';
  }

  return path.startsWith(`${basePath}/`)
    ? normalizePath(path.slice(basePath.length))
    : null;
}

function stripProxyApiBase(path: string): string {
  return normalizePath(path.replace(/^\/api\/[^/]+(?=\/)/, ''));
}

function getPathOnly(value: string): string {
  const trimmed = (value ?? '').trim();
  if (/^https?:\/\//i.test(trimmed)) {
    return getAbsoluteUrlPath(trimmed);
  }

  return trimmed.split(/[?#]/)[0];
}

function getAbsoluteUrlPath(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return url.split(/[?#]/)[0];
  }
}

function normalizePath(path: string): string {
  const trimmed = (path ?? '').trim();
  const withSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return withSlash.replace(/\/+$/, '') || '/';
}
